import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AccountService } from '../../../../../services/account/account.service';
import { CustomerService } from '../../../../../services/customer/customer.service';
import { ListResponse } from '../../../../../models/list-response';
import { EventType, EventTypeCategory} from '../../../../../models/event-type';
import { CustomerCredit, CustomerEventVoucher } from '../../../../../models/customer';
import { EventTypeService } from '../../../../../services/event-type/event-type.service';
import { batchAll } from '../../../../../utils';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { EventTypeStatus } from '../../../../../models/event-type-enums';

// TODO: Merge this with add-credit-or-voucher
@Component({
  selector: 'app-bulk-add-credit-or-voucher',
  templateUrl: './bulk-add-credit-or-voucher.component.html',
  styleUrls: ['./bulk-add-credit-or-voucher.component.css']
})
export class BulkAddCreditOrVoucherComponent implements OnInit {

  readonly TYPE_CREDIT = 'Credit';
  readonly TYPE_VOUCHER = 'Voucher';

  public customerIds: number[];
  public customerNames: {[key: number]: string};
  // This prevents the count from displaying incorrectly while loading.
  public customerIdCount: number;

  public eventTypeList: EventType[];
  public eventCategoryList: EventTypeCategory[];

  public selectedType = this.TYPE_CREDIT;
  public credit: CustomerCredit = {
    id: null,
    url: '',
    amount_total: null,
    amount_remaining: null,
    is_expired: false,
    is_removed: false,
    will_apply_to_recurring_payments: false,
    only_applies_to_recurring_payments: false,
    date_added_str: '',
    created_at: '',
    updated_at: ''
  };
  public voucher: CustomerEventVoucher = {
    id: null,
    url: '',
    event_type_id: null,
    event_type_url: '',
    event_type_name: '',
    event_category_id: null,
    event_category_url: '',
    event_category_name: '',
    number_of_uses_total: null,
    number_of_uses_remaining: null,
    is_expired: false,
    is_removed: false,
    date_added: '',
    date_added_str: '',
    created_at: '',
    updated_at: ''
  };
  public useVoucherEventType = true;

  // Used to display a progress bar.
  public requestsCompleted = 0;

  public serverError: number = null;

  public loadingCredit = false;
  public loadingVoucher = false;
  public loadingEventTypes = false;
  public loadingEventCategories = false;

  public creditErrors: any = {};
  public voucherErrors: any = {};
  public creditErrorMessage = '';
  public voucherErrorMessage = '';

  public EventTypeStatus = EventTypeStatus;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private router: Router,
    private customerService: CustomerService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // The customer IDs and names should have been placed in the state by the
    // customers component. If they are missing it is likely because the user
    // navigated directly to this URL. In this case, we redirect back to the
    // customers list.
    const missingRequiredState = (
      !history.state ||
      !history.state.customerIds ||
      !history.state.customerNames
    );
    if (missingRequiredState) {
      this.router.navigate(['/customers']);
    }

    this.customerIds = history.state.customerIds;
    this.customerNames = history.state.customerNames;
    this.customerIdCount = this.customerIds.length;

    this.loadEventTypes();
    this.loadEventCategories();
  }

  public handleCreditSubmit() {
    this.creditErrorMessage = '';
    this.creditErrors = {};

    if (!this.credit.amount_total) {
      this.creditErrors.amount_total = 'This field is required.';
      this.creditErrorMessage = 'Please correct the errors above and try again.';
    } else if (this.credit.amount_total < 0.01) {
      this.creditErrors.amount_total = 'This value must be at least 0.01.';
      this.creditErrorMessage = 'Please correct the errors above and try again.';
    }
    if (this.creditErrorMessage !== '') {
      return;
    }

    if (!this.credit.will_apply_to_recurring_payments) {
      this.credit.only_applies_to_recurring_payments = false;
    }

    this.loadingCredit = true;

    this.requestsCompleted = 0;
    let successfulCount = 0;
    const failedNames = [];

    const customerCreditSubmitHandler = (customerId: number): Promise<void> => {
      return this.customerService
        .addCustomerCredit(
          customerId,
          this.credit
        )
        .then(() => { successfulCount++; })
        .catch(error => {
          if (error.status === 403) {
            this.creditErrorMessage = 'You do not have permission to add credits.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          } else {
            failedNames.push(this.customerNames[customerId]);
          }
        })
        .finally(() => { this.requestsCompleted++; });
    };

    batchAll(
      4,
      this.customerIds,
      customerCreditSubmitHandler
    ).then(() => {
      if (successfulCount === 0) {
        if (this.creditErrorMessage === '') {
          this.creditErrorMessage = 'Unable to add credits.';
        }
        return;
      }

      let successMessage = (
        'Successfully added credit to ' + successfulCount.toString() + ' customers.'
      );
      if (failedNames.length > 0) {
        successMessage += ' Unable to add credit to the following customer';
        if (failedNames.length > 1) {
          successMessage += 's';
        }
        successMessage += ':';
        for (const failedName of failedNames) {
          successMessage += ' ' + failedName;
        }
      }

      this.router.navigate(
        ['/customers'],
        {
          state: {
            feedback: {
              text: successMessage,
              status: 'success'
            }
          }
        }
      );
    }).finally(() => { this.loadingCredit = false; });
  }

  public handleVoucherSubmit() {
    this.voucherErrorMessage = '';
    this.voucherErrors = {};

    if (this.useVoucherEventType && !this.voucher.event_type_id) {
      this.voucherErrors.event_type_id = 'This field is required.';
      this.voucherErrorMessage = 'Please correct the errors above and try again.';
    }
    if (!this.useVoucherEventType && !this.voucher.event_category_id) {
      this.voucherErrors.event_category_id = 'This field is required.';
      this.voucherErrorMessage = 'Please correct the errors above and try again.';
    }
    if (!this.voucher.number_of_uses_total) {
      this.voucherErrors.number_of_uses_total = 'This field is required.';
      this.voucherErrorMessage = 'Please correct the errors above and try again.';
    } else if (this.voucher.number_of_uses_total < 1) {
      this.voucherErrors.number_of_uses_total = 'This value must be at least 1.';
      this.voucherErrorMessage = 'Please correct the errors above and try again.';
    }
    if (this.voucherErrorMessage !== '') {
      return;
    }

    this.loadingVoucher = true;

    this.requestsCompleted = 0;
    let successfulCount = 0;
    const failedNames = [];

    const customerVoucherSubmitHandler = (customerId: number): Promise<void> => {
      return this.customerService
        .addCustomerEventVoucher(
          customerId,
          this.voucher
        )
        .then(() => {
          successfulCount++;
        }).catch(error => {
          if (error.status === 403) {
            this.voucherErrorMessage = 'You do not have permission to add vouchers.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          } else {
            failedNames.push(this.customerNames[customerId]);
          }
        })
        .finally(() => { this.requestsCompleted++; });
    };

    batchAll(
      4,
      this.customerIds,
      customerVoucherSubmitHandler
    ).then(() => {
      if (successfulCount === 0) {
        if (this.voucherErrorMessage === '') {
          this.voucherErrorMessage = 'Unable to add vouchers.';
        }
        return;
      }

      let successMessage = (
        'Successfully added vouchers to ' + successfulCount.toString() + ' customers.'
      );
      if (failedNames.length > 0) {
        successMessage += ' Unable to add vouchers to the following customer';
        if (failedNames.length > 1) {
          successMessage += 's';
        }
        successMessage += ':';
        for (const failedName of failedNames) {
          successMessage += ' ' + failedName;
        }
      }

      this.router.navigate(
        ['/customers'],
        {
          state: {
            feedback: {
              text: successMessage,
              status: 'success'
            }
          }
        }
      );
    }).finally(() => { this.loadingVoucher = false; });
  }

  public clearVoucherEventTypeAndCategory() {
    this.voucher.event_type_id = null;
    this.voucher.event_category_id = null;
  }

  public getProgressPercentageStr() {
    return Math.ceil((this.requestsCompleted / this.customerIds.length) * 100).toString();
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {}
      )
      .then(response => {
        this.eventTypeList = response.results.filter(et => et.status !== EventTypeStatus.draft);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventTypes = false;
      });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(
        1,
        3000
      )
      .then(response => {
        this.eventCategoryList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventCategories = false;
      });
  }

}
